import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { NAV, AUTH } from "@/constants/testIds";
import { LogOut, Sparkles, Gauge, Sliders, ScrollText, ClipboardList } from "lucide-react";

const LOGO_URL =
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_b0f0f9b1-18c4-4169-9d57-74f35eb2af07/artifacts/z5a1xd9x_PLAD_GOLD_png.webp";

function NavItem({ to, testId, icon: Icon, label }) {
    return (
        <NavLink
            to={to}
            data-testid={testId}
            className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                    isActive
                        ? "bg-[color:var(--profecia-gold-light)] text-[color:var(--profecia-gold)]"
                        : "text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)]"
                }`
            }
        >
            <Icon size={14} strokeWidth={1.9} />
            {label}
        </NavLink>
    );
}

export default function TopBar() {
    const { user, isAdmin, logout } = useAuth();
    const navigate = useNavigate();

    const doLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[color:var(--border-subtle)]">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
                <div className="flex items-center gap-4">
                    <img src={LOGO_URL} alt="Profecia Links" className="h-10 w-auto object-contain" />
                    <div className="gold-divider self-stretch" />
                    <div>
                        <h1 className="font-serif text-xl leading-none text-[color:var(--text-main)]">PromptForge</h1>
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)] mt-1">
                            by Profecia Links
                        </div>
                    </div>
                </div>

                <nav className="hidden md:flex items-center gap-1 ml-4">
                    <NavItem to="/tasks" testId="nav-tasks" icon={ClipboardList} label="Tasks" />
                    <NavItem to="/forge" testId={NAV.forgeLink} icon={Sparkles} label="Forge" />
                    {isAdmin && (
                        <>
                            <NavItem to="/governance" testId={NAV.governanceLink} icon={Gauge} label="Governance" />
                            <NavItem to="/admin/llm-config" testId={NAV.llmConfigLink} icon={Sliders} label="LLM Config" />
                            <NavItem to="/admin/prompts" testId={NAV.promptsLink} icon={ScrollText} label="Prompt Library" />
                        </>
                    )}
                </nav>

                {user && (
                    <div className="ml-auto flex items-center gap-3">
                        <div data-testid={NAV.userChip} className="text-right hidden sm:block">
                            <div className="text-xs text-[color:var(--text-main)] font-medium">{user.name || user.email}</div>
                            <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--profecia-gold)]">
                                {user.role === "admin" ? "Administrator" : "Content Creator"}
                            </div>
                        </div>
                        <button
                            data-testid={AUTH.logoutButton}
                            onClick={doLogout}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)] transition-colors text-sm"
                            title="Sign out"
                        >
                            <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}
